import { useNavigate } from 'react-router';
import { UUID } from '@myme/utils';
import { BinIcon, EmbedIcon, OverviewIcon, ReportIcon, ShareIcon, StarIcon } from 'src/icons';
import { usePalette, useShareMyme, useTranslation } from 'src/hooks';
import { mymeDetailsPageRoute } from 'src/routes/route-list';
import { Option } from 'src/components/molecules';
import { useRemoveMymeFromLibrary } from './useRemoveMymeFromLibrary';

interface Options {
  initializeReportFn?: () => void;
  openEmbedDialog?: () => void;
}

export const useMymePreviewOptions = (mymeId: UUID, options?: Options) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { error, warning } = usePalette();
  const handleCopyToClipboard = useShareMyme();
  const removeMymeFromLibrary = useRemoveMymeFromLibrary();

  const libraryOptions: Option[] = [
    {
      text: t('pages.library.options.overview'),
      icon: <OverviewIcon />,
      onClick: () => navigate(mymeDetailsPageRoute(mymeId))
    },
    {
      text: t('pages.library.options.share'),
      icon: <ShareIcon />,
      onClick: () => handleCopyToClipboard(mymeId)
    },
    {
      text: t('pages.library.options.review'),
      icon: <StarIcon fill={warning.main} />,
      onClick: async () => {
        await navigate(mymeDetailsPageRoute(mymeId));
        await document.getElementById('my-review')?.scrollIntoView();
      }
    },
    {
      text: t('pages.library.options.remove'),
      icon: <BinIcon stroke={error.main} />,
      color: error.main,
      onClick: () => removeMymeFromLibrary.mutate(mymeId)
    }
  ];

  const detailsOptions: Option[] = [
    {
      text: t('pages.mymes.details.menu_options.share'),
      icon: <ShareIcon />,
      onClick: () => handleCopyToClipboard(mymeId)
    },
    {
      text: t('pages.mymes.details.menu_options.embed'),
      icon: <EmbedIcon />,
      onClick: () => {
        if (options?.openEmbedDialog) {
          options.openEmbedDialog();
        }
      }
    },
    {
      text: t('pages.mymes.details.menu_options.report'),
      icon: <ReportIcon />,
      onClick: () => {
        if (options?.initializeReportFn) {
          options.initializeReportFn();
        }
      }
    }
  ];

  return { libraryOptions, detailsOptions };
};
